import z from "zod/v4";
import { TenantId } from "./tenant-id";
import { SiteId } from "./site-id";
import { AssetId } from "./asset-id";
import { CameraId } from "./camera-id";
import { AssetCameraKey } from "./asset-camera-key";
import type { InstalledCamera } from "./installed-camera";

// Local codec definitions to avoid circular dependency with codecs.ts
const TenantIdCodec = z.codec(z.string(), z.custom<TenantId>(), {
  decode: (s) => TenantId.fromString(s),
  encode: (id) => TenantId.toString(id),
});

const SiteIdCodec = z.codec(z.string(), z.custom<SiteId>(), {
  decode: (s) => SiteId.fromString(s),
  encode: (id) => SiteId.toString(id),
});

const AssetIdCodec = z.codec(z.string(), z.custom<AssetId>(), {
  decode: (s) => AssetId.fromString(s),
  encode: (id) => AssetId.toString(id),
});

const CameraIdCodec = z.codec(z.string(), z.custom<CameraId>(), {
  decode: (s) => CameraId.fromString(s),
  encode: (id) => CameraId.toString(id),
});

const InstalledCameraSchema = z.object({
  tenant: TenantIdCodec,
  site: SiteIdCodec,
  asset: AssetIdCodec,
  camera: CameraIdCodec,
});

/**
 * Codec for topic format: `{tenant}/{site}/{asset}/{camera}`
 *
 * Example: "T2277CC8/S0-0/A2-G2-CAM_11241/camera-a"
 *
 * This matches the format used in Topic.camera() from @visioni/a3s-messages
 * (without the G2/ prefix).
 */
export const InstalledCameraInputTopic = z.codec(z.string(), z.custom<InstalledCamera>(), {
  decode: (str, ctx) => {
    const parts = str.split("/");
    const [tenantStr, siteStr, assetStr, cameraStr, ...rest] = parts;
    if (
      tenantStr === undefined ||
      siteStr === undefined ||
      assetStr === undefined ||
      cameraStr === undefined ||
      rest.length > 0
    ) {
      ctx.issues.push({
        code: "custom",
        message: `Expected topic format: {tenant}/{site}/{asset}/{camera}, got ${String(parts.length)} parts`,
        input: str,
      });
      return z.NEVER;
    }

    try {
      return {
        tenant: TenantId.fromString(tenantStr),
        site: SiteId.fromString(siteStr),
        asset: AssetId.fromString(assetStr),
        camera: CameraId.fromString(cameraStr),
      };
    } catch (err: unknown) {
      ctx.issues.push({
        code: "custom",
        message: err instanceof Error ? err.message : "Invalid topic format",
        input: str,
      });
      return z.NEVER;
    }
  },
  encode: (camera) =>
    `${TenantId.toString(camera.tenant)}/${SiteId.toString(camera.site)}/${AssetId.toString(camera.asset)}/${CameraId.toString(camera.camera)}`,
});

/**
 * Codec for URL format: `tenants/{tenant}/sites/{site}/cameras/{asset}@{camera}`
 *
 * Example: "tenants/T2277CC8/sites/S0-0/cameras/A2-G2-CAM_11216@camera-b"
 */
export const InstalledCameraInputUrl = z.codec(z.string(), z.custom<InstalledCamera>(), {
  decode: (str, ctx) => {
    const match = str.match(/^tenants\/([^/]+)\/sites\/([^/]+)\/cameras\/(.+)$/);
    const tenantStr = match?.[1];
    const siteStr = match?.[2];
    const assetCameraKeyStr = match?.[3];
    if (tenantStr === undefined || siteStr === undefined || assetCameraKeyStr === undefined) {
      ctx.issues.push({
        code: "custom",
        message: "Expected URL format: tenants/{tenant}/sites/{site}/cameras/{asset}@{camera}",
        input: str,
      });
      return z.NEVER;
    }

    try {
      const tenant = TenantId.fromString(tenantStr);
      const site = SiteId.fromString(siteStr);
      const assetCameraKey = AssetCameraKey.fromString(assetCameraKeyStr);
      const asset = AssetCameraKey.asset(assetCameraKey);
      const camera = AssetCameraKey.camera(assetCameraKey);

      return { tenant, site, asset, camera };
    } catch (err: unknown) {
      ctx.issues.push({
        code: "custom",
        message: err instanceof Error ? err.message : "Invalid URL format",
        input: str,
      });
      return z.NEVER;
    }
  },
  encode: (camera) =>
    `tenants/${TenantId.toString(camera.tenant)}/sites/${SiteId.toString(camera.site)}/cameras/${AssetCameraKey.toString(AssetCameraKey.fromParts(camera.asset, camera.camera))}`,
});

/**
 * Codec for positional tuple format: [tenant, site, asset, camera]
 *
 * Example: ["T2277CC8", "S0-0", "A2-G2-CAM_11241", "camera-a"]
 */
export const InstalledCameraInputTuple = z.codec(
  z.tuple([TenantIdCodec, SiteIdCodec, AssetIdCodec, CameraIdCodec]),
  z.custom<InstalledCamera>(),
  {
    decode: (tuple) => ({
      tenant: tuple[0],
      site: tuple[1],
      asset: tuple[2],
      camera: tuple[3],
    }),
    encode: (camera): [TenantId, SiteId, AssetId, CameraId] => [
      camera.tenant,
      camera.site,
      camera.asset,
      camera.camera,
    ],
  },
);

/**
 * Discriminated codec that accepts any of the supported input formats
 * and decodes to InstalledCamera.
 *
 * Supported formats:
 * 1. Object: `{ tenant, site, asset, camera }`
 * 2. Tuple: `[tenant, site, asset, camera]`
 * 3. Topic string: `{tenant}/{site}/{asset}/{camera}`
 * 4. URL string: `tenants/{tenant}/sites/{site}/cameras/{asset}@{camera}`
 *
 * @example
 * // Object format
 * InstalledCameraInput.parse({ tenant: "T2277CC8", site: "S0-0", asset: "A2-G2-CAM_11241", camera: "camera-a" })
 *
 * @example
 * // Tuple format
 * InstalledCameraInput.parse(["T2277CC8", "S0-0", "A2-G2-CAM_11241", "camera-a"])
 *
 * @example
 * // Topic format
 * InstalledCameraInput.parse("T2277CC8/S0-0/A2-G2-CAM_11241/camera-a")
 *
 * @example
 * // URL format
 * InstalledCameraInput.parse("tenants/T2277CC8/sites/S0-0/cameras/A2-G2-CAM_11216@camera-b")
 */
export const InstalledCameraInput = z.codec(
  z.union([
    z.object({ tenant: TenantIdCodec, site: SiteIdCodec, asset: AssetIdCodec, camera: CameraIdCodec }),
    z.tuple([TenantIdCodec, SiteIdCodec, AssetIdCodec, CameraIdCodec]),
    z.string(),
  ]),
  z.custom<InstalledCamera>(),
  {
    decode: (input, ctx) => {
      // Object format (non-array object)
      if (typeof input === "object" && !Array.isArray(input)) {
        const result = InstalledCameraSchema.safeParse(input);
        if (!result.success) {
          ctx.issues.push({
            code: "custom",
            message: result.error.issues.map((i) => i.message).join("; "),
            input,
          });
          return z.NEVER;
        }
        return result.data;
      }

      // Tuple format
      if (Array.isArray(input)) {
        const result = InstalledCameraInputTuple.safeParse(input);
        if (!result.success) {
          ctx.issues.push({
            code: "custom",
            message: result.error.issues.map((i) => i.message).join("; "),
            input,
          });
          return z.NEVER;
        }
        return result.data;
      }

      // String formats - try topic first, then URL
      if (typeof input === "string") {
        // URL format check (more specific pattern)
        if (input.startsWith("tenants/")) {
          const result = InstalledCameraInputUrl.safeParse(input);
          if (!result.success) {
            ctx.issues.push({
              code: "custom",
              message: result.error.issues.map((i) => i.message).join("; "),
              input,
            });
            return z.NEVER;
          }
          return result.data;
        }

        // Topic format (4 parts separated by /)
        const result = InstalledCameraInputTopic.safeParse(input);
        if (!result.success) {
          ctx.issues.push({
            code: "custom",
            message: result.error.issues.map((i) => i.message).join("; "),
            input,
          });
          return z.NEVER;
        }
        return result.data;
      }

      ctx.issues.push({
        code: "custom",
        message: "Expected object, tuple, topic string, or URL string",
        input,
      });
      return z.NEVER;
    },
    encode: (camera) => ({
      tenant: camera.tenant,
      site: camera.site,
      asset: camera.asset,
      camera: camera.camera,
    }),
  },
);

/**
 * Input type for InstalledCameraInput codec.
 *
 * Accepts:
 * - Object: `{ tenant, site, asset, camera }` (with branded or string types)
 * - Tuple: `[tenant, site, asset, camera]`
 * - Topic string: `{tenant}/{site}/{asset}/{camera}`
 * - URL string: `tenants/{tenant}/sites/{site}/cameras/{asset}@{camera}`
 */
export type InstalledCameraInput = z.input<typeof InstalledCameraInput>;
